import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, QuickAddVariantConfirmedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    await this.openModal();
  };

  /**
   * Opens the modal in "variant selection only" mode (no add-to-cart button). Used by the
   * selected-variant text on the card: picking a new variant here updates the card's own
   * swatch selection, so the card's normal Add button ends up using the right variant.
   *
   * Stops propagation so this click doesn't also trigger anything the card is nested in (e.g. a
   * product-accessories `<label>`, which would otherwise toggle its checkbox/radio selection).
   * @param {Event} event - The click event
   */
  openVariantSelector = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await this.openModal({ hideAddToCart: true });
  };

  /**
   * Fetches (or reuses cached) product page content, morphs it into the modal, and opens it.
   * @param {object} [options]
   * @param {boolean} [options.hideAddToCart] - Whether to hide the add-to-cart button in the modal.
   */
  async openModal({ hideAddToCart = false } = {}) {
    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    await this.#openQuickAddModal(hideAddToCart);
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * @param {boolean} [hideAddToCart] - Whether to hide the add-to-cart button in the modal.
   */
  #openQuickAddModal = async (hideAddToCart = false) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    // The modal is a singleton shared by every product card. closeDialog() is async — it waits
    // out the close animation before actually closing the <dialog> element — so if this product
    // is opened while a *different* product's modal is still mid-close, the <dialog> is still
    // technically open: showDialog() would silently no-op below, and the in-flight close would
    // then close this new content instead of showing it. Wait for it to fully close first.
    if (dialogComponent.refs.dialog?.open) {
      await new Promise((resolve) => {
        dialogComponent.addEventListener(DialogCloseEvent.eventName, () => resolve(undefined), { once: true });
      });
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.setVariantOnlyMode(hideAddToCart ? this.closest('product-card') : null, hideAddToCart);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Flatten the fetched product-details markup down to just a title+price header, the
    // variant picker, and the product form (quantity, purchase options, buy buttons) — on both
    // mobile and desktop. The modal never shows media (see stylesheet below) and every current
    // entry point opens it in variant-selection-only mode, so the full multi-block product-page
    // layout (accordion, complementary products, etc.) it used to keep intact on desktop only
    // added visual clutter and a lot of CSS just to hide most of it back out again.
    {
      productGrid.querySelector('.product-information__media')?.remove();

      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // The select button lives inside #quick-add-modal-content (so it's part of the same
    // scrollable area as the rest of the content), but it isn't part of the fetched product
    // page. Detach it before morphing — rather than letting morph() implicitly discard it as an
    // unmatched extra child, which depends on it landing at a childNodes index productGrid has
    // nothing for — and re-attach it as the last child afterward, deterministically.
    const selectButton = modalContent.querySelector('.quick-add-modal__select-button');
    selectButton?.remove();

    morph(modalContent, productGrid);

    if (selectButton) {
      modalContent.appendChild(selectButton);
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {Element | null} */
  #sourceProductCard = null;

  /** @type {{id: string, options?: string[]} | null} */
  #latestVariant = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#trackLatestVariant);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Toggles "variant selection only" mode: hides the add-to-cart button and shows the "Select"
   * button instead. While active, confirming a selection (via that button) mirrors the picked
   * variant back onto the product card that opened the modal.
   * @param {Element | null} productCard - The product card to sync the selection back into.
   * @param {boolean} hideAddToCart
   */
  setVariantOnlyMode(productCard, hideAddToCart) {
    this.#sourceProductCard = hideAddToCart ? productCard : null;
    this.#latestVariant = null;

    const modalContent = document.getElementById('quick-add-modal-content');
    modalContent?.toggleAttribute('data-hide-add-to-cart', hideAddToCart);
  }

  /**
   * Keeps track of the variant currently selected in the modal, without acting on it — the
   * selection is only applied once the customer explicitly confirms it (see
   * `confirmVariantSelection`).
   * @param {VariantUpdateEvent} event
   */
  #trackLatestVariant = (event) => {
    this.#latestVariant = event.detail?.resource ?? null;
  };

  /**
   * Handles the "Select" button in variant-selection-only mode: confirms whichever variant is
   * currently selected in the modal (mirroring it back onto the product card that opened the
   * modal, if any), notifies other listeners (e.g. product-accessories), and closes the dialog.
   */
  confirmVariantSelection = () => {
    const variant = this.#latestVariant;

    if (variant?.id && this.#sourceProductCard) {
      // Keep the card's own swatches (if any) in sync, so a later re-open of this modal, or the
      // card's own Add button, use the confirmed variant.
      const cardInput = /** @type {HTMLInputElement | null} */ (
        this.#sourceProductCard.querySelector(`input[type="radio"][data-variant-id="${variant.id}"]`)
      );

      if (cardInput && !cardInput.checked) {
        cardInput.checked = true;
        cardInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update the "Color: Red, Size: M" text directly. This works even when the card has no
      // swatches to trigger the usual variant-update pipeline above.
      this.#updateVariantInfoText(this.#sourceProductCard, variant);
    }

    this.dispatchEvent(new QuickAddVariantConfirmedEvent(variant?.id));

    this.closeDialog();
  };

  /**
   * Updates the product-variant-info block(s) on a product card to display a given variant's
   * option values, in place, without needing a full re-render.
   * @param {Element} productCard
   * @param {{options?: string[]}} variant
   */
  #updateVariantInfoText(productCard, variant) {
    if (!Array.isArray(variant.options)) return;

    for (const info of productCard.querySelectorAll('.product-variant-info')) {
      const values = info.querySelectorAll('.product-variant-info__value');
      values.forEach((valueEl, index) => {
        if (variant.options?.[index] != null) {
          valueEl.textContent = variant.options[index];
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
